<script lang="ts">
  // libs
  import { onMount } from "svelte";
  import * as _ from "lodash";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import { saveForLater } from "@retailer/svelte/Cart/SavedForLater/Api.svelte";
  import Flash from "@shared/services/flashv2.js";
  import { get as getStoreValue } from "svelte/store";
  import { CartOrderEntity, ProductEntity } from "@shared/services/event_tracking/entities";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";

  // components
  import ProductContainerDetails from "@shared/filters/product_container_details.js";
  import ProductImage from "@shared/svelte/Components/ProductImage.svelte";
  import CartDealModal from "../CartDealModal.svelte";
  import CartProductPrice from "@retailer/svelte/Cart/Page/DistributorSection/CartProductPrice.svelte";
  import PickupOrDeliveryWrapper from "@retailer/svelte/Cart/Page/PickupOrDelivery/PickupOrDeliveryWrapper.svelte";
  import CartPageCartWidgetContainer from "@retailer/svelte/CartWidget/v2/Containers/CartPageCartWidgetContainer.svelte";

  // stores
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import FuturePricesStore from "@shared/svelte/Stores/FuturePricesStore";
  import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";

  // services
  import ProductAvailabilityWrapper from "@retailer/svelte/Cart/ProductAvailabilityWrapper.svelte";
  import RemoveModal from "@retailer/svelte/Cart/RemoveModal.svelte";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";
  import { isOHLQOrder } from "@retailer/services/order_fulfillment_service";
  import { getDataForInventory } from "@retailer/services/pricing_data_service";

  // types
  import type { SavedProductDict } from "@shared/types/SavedProducts";
  import type { CartProduct, InventoryPrice } from "@shared/types/ProductLine";
  import type { CartOrderType } from "@shared/types/Cart";
  import type { DistributorCart } from "@shared/types/Distributor";
  import type { PricingDataResult } from "@shared/types/Pricing";

  // props
  export let product: CartProduct;
  export let Cart: any;
  export let customPricingMode: any;
  export let order: CartOrderType;
  export let callbacks: any;
  export let pindex: number;
  export let showLoading: boolean;

  let distributor: DistributorCart = order.distributor;
  let productApiUpdateInProgress: boolean = false;
  let showDealPopover: boolean = false;
  let showRemoveModal: boolean = false;
  let disableInputs = false;
  let productNotes = product.deal_notes || product.permanent_deal_notes?.notes;

  onMount(() => {
    InventoryItemStore.fetchInventoryItem(product.inventory_id, cartProductWarehouseLocationCode);
  });

  function withApiUpdateTracking(promise) {
    if (!promise?.finally) return promise;

    productApiUpdateInProgress = true;
    return promise.finally(() => (productApiUpdateInProgress = false));
  }

  function removeItem(skipPlaceholder: any) {
    if (showLoading == true || productApiUpdateInProgress == true) {
      return;
    }

    const removed = { ...product, stockLevel: widgetItem?.stock_info?.level };

    return withApiUpdateTracking(Cart.removeProduct(removed, null, skipPlaceholder));
  }

  function saveForLaterWrapper(product) {
    if (showLoading == true || productApiUpdateInProgress == true) {
      return;
    }

    return withApiUpdateTracking(saveForLater(Cart, product, saveForLaterCallback));
  }

  const saveForLaterCallback = (sp: SavedProductDict) => {
    Flash.now({
      message: `<p style="display:inline" class="fizz-text-strong">${product.product.name}</p>	
            <p style="display:inline"> was moved to saved for later</p>`,
      type: "success",
      rawHtml: true,
    });
    callbacks.redoSavedProducts(sp);
  };

  function onNoteUpdate(event) {
    const product = event.detail.cartProduct;
    productNotes = product.deal_notes || product.permanent_deal_notes?.notes;
  }

  let futurePrices = getStoreValue(FuturePricesStore);
  let containerCaseSize = product.product.container_case_size;

  let pricingForProduct, caseLevelPrice, casePriceForDisplay, oneBottlePrice;
  let showMinBottle = false;

  let baseUnitPrice, baseCasePrice;
  const getInventoryPrices = async () => {
    let pricingData: PricingDataResult = await getDataForInventory(product.inventory_id);
    let price: InventoryPrice = pricingData?.inventory_price;
    if (price) {
      baseUnitPrice = price.unit_price;
      baseCasePrice = price.case_price;
    }
  };
  getInventoryPrices();

  $: {
    pricingForProduct = futurePrices.find(p => p.inventory_id === product.inventory_id);
    if (pricingForProduct) {
      oneBottlePrice = pricingForProduct.current_prices.find(p => p.bottles == 1);

      if (containerCaseSize && containerCaseSize > 1) {
        caseLevelPrice = pricingForProduct.current_prices.find(p => p.bottles == containerCaseSize);
      }
    }
    casePriceForDisplay = caseLevelPrice || oneBottlePrice;
    showMinBottle = Boolean(oneBottlePrice && caseLevelPrice);
  }

  $: {
    disableInputs = showLoading || productApiUpdateInProgress;
  }
  $: isMobile = $LayoutStore.isMobile;
  $: hasUnitDiscount =
    product.discount_amount > 0 &&
    baseUnitPrice &&
    baseUnitPrice !== product.unit_price_at_purchase;
  $: hasCaseDiscount =
    product.discount_amount > 0 &&
    baseCasePrice &&
    baseCasePrice !== product.case_price_at_purchase;
  $: bottlesOrdered = product.unit_quantity > 0;
  $: casesOrdered = product.case_quantity > 0;
  $: widgetItem = $InventoryItemStore?.inventoryItems[product.inventory_id];

  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );

  // auth service
  let authService = getControlStateAuthenticationService();

  let authStatus;

  $: hideAllDistributorInfo = authService?.shouldHideAllDistributorInfo?.(
    $RetailerUserAuthenticationsStore,
    [distributor.id],
  );

  $: authStatus = authService?.getRetailerUserAuthenticationStatus?.(
    $RetailerUserAuthenticationsStore,
    [distributor.id],
  );

  $: requestedItem = product.requested_item;

  $: imageClasses = requestedItem
    ? "fizz-bordered fizz-radius-medium fizz-surface request-image"
    : "";
  $: imageWidth = requestedItem ? 85 : 50;
  $: imageHeight = requestedItem ? 85 : 70;
  $: canUseWineCellar =
    stagedRolloutCheck("marketplace__fulfillment_methods") && distributor?.uses_wine_cellar;
  $: cartProductWarehouseLocationCode = canUseWineCellar
    ? product.warehouse_location_code || distributor?.default_stock_site_id
    : null;
</script>

<style>
  :global.request-image {
    padding: 0.25rem;
  }
</style>

<div
  class:fizz-inset-square-24={!requestedItem}
  class:fizz-surface-gray={requestedItem}
  class:fizz-inset-vertical-12={requestedItem}
>
  <div class="fizz-flex-row" class:fizz-stack-12={productNotes}>
    <div class="fizz-max-content fizz-inline-16 fizz-align-self-start">
      <ProductImage
        className={imageClasses}
        fizz={true}
        width={imageWidth}
        height={imageHeight}
        product={product.product_line}
      />
    </div>

    <div class="fizz-flex-column fizz-flex-item">
      <div
        class="fizz-flex-row-responsive fizz-space-between"
        class:fizz-stack-12={!$LayoutStore.isMobile}
      >
        <div class="fizz-flex-row" class:fizz-stack-8={$LayoutStore.isMobile}>
          <div class="media-fluid align--left align-self-center">
            <div class="fizz-text-medium">
              <!-- svelte-ignore a11y-missing-attribute -->
              <UIRouterLink
                stateName="layout.product_detail"
                params={{ productId: product.product_line.id }}
                eventName="cart_product_redirect"
                useTrackClick
                trackingParams={[
                  new CartOrderEntity({ distributorId: distributor.id, orderId: order.id }),
                  new ProductEntity({ productId: product.id }),
                ]}
              >
                <a class="cursor-pointer notranslate fizz-text-strong" style="color: black;">
                  {product.product_line.name}
                </a>
              </UIRouterLink>
            </div>

            <div class="fizz-text-small">
              {ProductContainerDetails()(
                product.product,
                null,
                null,
                true,
              )}{#if !!product.inventory.sku && !hideAllDistributorInfo}&nbsp;(SKU: {product
                  .inventory.sku}){/if}
            </div>

            {#if !hideAllDistributorInfo}
              <div
                class="fizz-text-small fizz-text-subdued"
                class:fizz-stack-16={$LayoutStore.isMobile}
                data-testid="product-pricing-breakdown"
              >
                {#if showMinBottle}
                  <span><FormatMoney input={oneBottlePrice.price_per_unit} /> min bottle</span>
                {:else if product.unit_price_at_purchase && bottlesOrdered}
                  {#if hasUnitDiscount}
                    <FormatMoney input={product.unit_price_at_purchase} />
                    <span data-testid="unit-price-discount-subtotal" class="strikethrough">
                      <FormatMoney input={baseUnitPrice} />
                    </span>/{product.product.container_type}
                  {:else}
                    <span>
                      <FormatMoney input={product.unit_price_at_purchase} />/{product.product
                        .container_type}
                    </span>
                  {/if}
                {/if}
                {#if product.case_price_at_purchase && casesOrdered}
                  {#if (product.unit_price_at_purchase && bottlesOrdered) || showMinBottle}
                    -
                  {/if}
                  {#if hasCaseDiscount && !$StateSettingsStore["uses_future_pricing_with_min_bottle"]}
                    <FormatMoney input={product.case_price_at_purchase} />
                    <span data-testid="case-price-discount-subtotal" class="strikethrough">
                      <FormatMoney input={baseCasePrice} />
                    </span>/case
                  {:else}
                    <FormatMoney input={product.case_price_at_purchase} />/case
                  {/if}
                {/if}
              </div>

              <!-- Mobile Pricing -->
              {#if !customPricingMode[distributor.id] && isMobile && !hideAllDistributorInfo}
                <CartProductPrice bind:product />
                <ProductAvailabilityWrapper
                  {product}
                  showWhenProductHasErrors={false}
                  {distributor}
                  location={"cart"}
                />
              {/if}
              <!-- // Mobile Pricing -->
            {/if}
          </div>
        </div>

        <!-- Desktop Pricing -->
        {#if !customPricingMode[distributor.id] && !isMobile}
          <div class="fizz-text-right fizz-align-self-start">
            <CartProductPrice bind:product />
          </div>
        {/if}
        <!-- // Desktop Pricing -->
      </div>

      {#if !customPricingMode[distributor.id] && !hideAllDistributorInfo}
        {#if (!product.inventory_is_completely_out_of_stock || canUseWineCellar) && !isOHLQOrder(distributor.id)}
          <div class="fizz-text-left">
            <PickupOrDeliveryWrapper
              cartProduct={product}
              {Cart}
              distributor={order.distributor}
              justify={"start"}
            />
          </div>
        {/if}

        <div class="fizz-flex-item fizz-flex-row-responsive fizz-space-between fizz-flex-wrap">
          <CartPageCartWidgetContainer
            inventoryId={product.inventory?.id}
            {order}
            {product}
            productIndex={pindex}
            {canUseWineCellar}
            {cartProductWarehouseLocationCode}
          />

          <ProductAvailabilityWrapper
            {product}
            showWhenProductHasErrors={false}
            {distributor}
            location={"cart"}
          >
            <div class="fizz-text-small fizz-nowrap" class:fizz-text-right={!$LayoutStore.isMobile}>
              {#if distributor.allow_retailer_deals}
                {#if !product.deal_notes && !(product.permanent_deal_notes || {}).notes}
                  <a
                    class="fizz-inline-block fizz-inline-8 fizz-text-subdued"
                    class:button--disabled-link={disableInputs}
                    href={"#"}
                    on:click|preventDefault={() => (showDealPopover = true)}
                    id="lg-note-button"
                  >
                    <u>Add deal note</u>
                  </a>
                {:else}
                  <a
                    class="fizz-inline-block fizz-inline-8 fizz-text-subdued"
                    class:button--disabled-link={disableInputs}
                    href={"#"}
                    on:click|preventDefault={() => (showDealPopover = true)}
                    id="saved-note-button"
                  >
                    <u> Edit deal note </u>
                  </a>
                {/if}
              {/if}
              <a
                class="fizz-inline-block fizz-inline-8 fizz-text-subdued"
                class:button--disabled-link={disableInputs}
                href={"#"}
                on:click|preventDefault={() => saveForLaterWrapper(product)}
                id="save-for-later-button"
              >
                <u>Save for later</u>
              </a>
              <a
                class="fizz-inline-block fizz-text-subdued"
                class:button--disabled-link={disableInputs}
                href={"#"}
                on:click|preventDefault={() => (showRemoveModal = true)}
                id="remove-button"
                data-testid="remove-from-cart"
              >
                <u>Remove</u>
              </a>
            </div>
          </ProductAvailabilityWrapper>

          <ProductAvailabilityWrapper
            {product}
            showWhenProductHasErrors={true}
            {distributor}
            location={"cart"}
          >
            <a
              id="remove-button"
              data-testid="remove-from-cart"
              class="fizz-inline-block fizz-text-subdued fizz-text-small"
              class:fizz-text-right={!$LayoutStore.isMobile}
              href={"#"}
              on:click|preventDefault={() => removeItem(true)}
            >
              <u>Remove</u>
            </a>
          </ProductAvailabilityWrapper>
        </div>
      {/if}

      {#if hideAllDistributorInfo}
        <div class="fizz-flex-item fizz-flex-row-4">
          {#if authStatus == "pending"}
            <Icon type="InfoYellow" color="red" height={16} width={16} />
            <div class="fizz-text-subdued">
              {authService?.pendingDescription}
            </div>
          {:else if authStatus == "invalid"}
            <Icon type="Error" color="red" height={16} width={16} />
            <div class="fizz-text-negative">
              {authService?.invalidDescription}
            </div>
          {/if}
        </div>

        <div
          class="fizz-flex-item fizz-flex-row-responsive fizz-justify-end fizz-space-between fizz-flex-wrap"
        >
          <a
            class="fizz-inline-block fizz-inline-8 fizz-text-small fizz-text-subdued"
            class:button--disabled-link={disableInputs}
            href={"#"}
            on:click|preventDefault={() => saveForLaterWrapper(product)}
            id="save-for-later-button"
          >
            <u>Save for later</u>
          </a>
          <a
            id="remove-button"
            data-testid="remove-from-cart"
            class="fizz-inline-block fizz-text-subdued fizz-text-small"
            class:fizz-text-right={!$LayoutStore.isMobile}
            href={"#"}
            on:click|preventDefault={() => removeItem(true)}
          >
            <u>Remove</u>
          </a>
        </div>
      {/if}
    </div>
  </div>

  {#if productNotes}
    <div class="fizz-surface-yellow fizz-inset-squish-12 fizz-text-small fizz-radius-medium">
      <p class="fizz-text-strong" style="display: inline;">Deal note:</p>
      <p style="display: inline;">{productNotes}</p>
    </div>
  {/if}
</div>

<RemoveModal
  on:delete={removeItem}
  bind:cartProduct={product}
  bind:show={showRemoveModal}
  location={"cart"}
/>
{#if distributor.allow_retailer_deals}
  <div class="fizz-relative">
    <CartDealModal
      bind:cartProduct={product}
      bind:show={showDealPopover}
      on:update={onNoteUpdate}
    />
  </div>
{/if}
